import { useEffect } from "react";

/** Set on a placed row, or a box drawn on the PDF, whose field is one of ours. */
export const SERVER_VARIABLE_ATTRIBUTE = "data-server-variable";
/** Set on a server variable that has not been drawn on the document yet. */
export const UNPLACED_SERVER_VARIABLE_ATTRIBUTE = "data-unplaced-server-variable";

/** The slice of a Vue vnode/instance this file reads. Vue is the builder's, not ours. */
interface Vnode {
  el?: unknown;
  component?: VueInstance | null;
  children?: unknown;
}
interface VueInstance {
  type?: { name?: string };
  props?: {
    field?: {
      name?: string;
      areas?: { page?: number | null; attachment_uuid?: string | null }[];
    } | null;
  };
  subTree?: Vnode;
}

interface BuilderFieldElement {
  el: Element;
  name: string;
  kind: "area" | "row";
  placed: boolean;
}

/** Read field rows and PDF areas from DocuSeal's Vue component tree. */
function fieldElements(builder: Element): BuilderFieldElement[] {
  const elements: BuilderFieldElement[] = [];
  const visit = (vnode: Vnode | null | undefined) => {
    if (!vnode) return;
    const instance = vnode.component;
    if (instance) {
      const el = instance.subTree?.el;
      const field = instance.props?.field;
      if (el instanceof Element && field) {
        if (instance.type?.name === "FieldArea") {
          elements.push({ el, name: field.name?.trim() ?? "", kind: "area", placed: true });
        } else if (instance.type?.name === "TemplateField") {
          // A drawn area has a page index (0 is the first page). Requiring an
          // attachment id too treated those rows as undrawn and hid their settings.
          const placed = Boolean(
            field.areas?.some(
              (area) => typeof area.page === "number" || Boolean(area.attachment_uuid)
            )
          );
          elements.push({ el, name: field.name?.trim() ?? "", kind: "row", placed });
        }
      }
      visit(instance.subTree);
    }
    if (Array.isArray(vnode.children)) vnode.children.forEach((child) => visit(child as Vnode));
  };
  const root = (builder as { component?: { $?: VueInstance } }).component?.$;
  visit(root?.subTree);
  return elements;
}

/**
 * A placed row in the builder carries its field's name only as text, and a box drawn on the
 * PDF only once selected, so CSS cannot tell one of our server-filled variables from a field
 * the admin added. Tag the rows and boxes whose name is a variable; the theme keys its icon
 * off the tag.
 *
 * The builder attaches its (open) shadow root once its script has loaded, which nothing
 * announces, so poll for it. That also picks up a rebuilt builder.
 */
export function useServerVariableMarks(root: HTMLElement | null, names: readonly string[]) {
  const namesKey = names.join("\n");

  useEffect(() => {
    if (!root) return;
    const variables = new Set(namesKey ? namesKey.split("\n") : []);
    let builder: Element | null = null;
    let observed: ShadowRoot | null = null;
    let observer: MutationObserver | undefined;

    const mark = () => {
      observed?.querySelectorAll(".list-field").forEach((row) => {
        const name = row.querySelector("[contenteditable]")?.textContent?.trim() ?? "";
        row.toggleAttribute(SERVER_VARIABLE_ATTRIBUTE, variables.has(name));
      });
      if (builder) {
        fieldElements(builder).forEach(({ el, name, kind, placed }) => {
          const variable = variables.has(name);
          el.toggleAttribute(SERVER_VARIABLE_ATTRIBUTE, variable);
          if (kind === "row") {
            el.toggleAttribute(UNPLACED_SERVER_VARIABLE_ATTRIBUTE, variable && !placed);
          }
        });
      }
    };

    const attach = () => {
      builder = root.querySelector("docuseal-builder");
      const shadow = builder?.shadowRoot ?? null;
      if (shadow === observed) return;
      observer?.disconnect();
      observed = shadow;
      if (!shadow) return;
      // Not attributes: marking a row must not retrigger this.
      observer = new MutationObserver(mark);
      observer.observe(shadow, { childList: true, subtree: true, characterData: true });
      mark();
    };

    attach();
    const poll = window.setInterval(attach, 250);
    return () => {
      window.clearInterval(poll);
      observer?.disconnect();
    };
  }, [root, namesKey]);
}
